// Ricerca nell'header: indirizzo (Google, su Invio) oppure coordinate "lat, lng".
// Ogni ricerca di indirizzo è una chiamata a pagamento alle API Google, per
// questo i suggerimenti partono solo premendo Invio e non a ogni tasto.

import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import Icon from "./Icon";
import { useToast } from "./Toast";
import { BACKEND } from "../lib/config";
import { getJson, postJson } from "../lib/http";
import { reticleIcon } from "../lib/basemap";

const COORDS_RE = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

export default function AddressSearch({ map, bounds }) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setSuggestions([]);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Il mirino appartiene alla mappa corrente: al cambio di comune si perde.
  useEffect(() => () => { markerRef.current = null; }, [map]);

  const goTo = (lat, lng, label) => {
    if (!map) return;
    const latlng = L.latLng(lat, lng);
    if (bounds && !bounds.contains(latlng)) {
      toast.error("Il punto cercato è fuori dal territorio comunale.");
      return;
    }
    if (markerRef.current) map.removeLayer(markerRef.current);
    markerRef.current = L.marker(latlng, { icon: reticleIcon, interactive: !!label }).addTo(map);
    if (label) markerRef.current.bindTooltip(label, { direction: "top", offset: [0, -12] });
    map.setView(latlng, Math.max(map.getZoom(), 18));
  };

  const onKeyDown = async (e) => {
    if (e.key === "Escape") { setSuggestions([]); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const text = query.trim();
    const m = text.match(COORDS_RE);
    if (m) {
      const lat = parseFloat(m[1]);
      const lng = parseFloat(m[2]);
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) { toast.error("Coordinate non valide."); return; }
      goTo(lat, lng);
      return;
    }
    if (text.length < 3) return;
    setBusy(true);
    try {
      const data = await postJson(`${BACKEND}/api/autocomplete`, { input: text });
      const list = (data.suggestions || [])
        .map((s) => s.placePrediction?.text?.text)
        .filter(Boolean);
      setSuggestions(list);
      if (!list.length) toast.info("Nessun indirizzo trovato.");
    } catch {
      toast.error("Ricerca indirizzi non disponibile al momento.");
    } finally {
      setBusy(false);
    }
  };

  const pick = async (text) => {
    setQuery(text);
    setSuggestions([]);
    try {
      const geo = await getJson(`${BACKEND}/api/geocode?address=${encodeURIComponent(text)}`);
      goTo(geo.lat, geo.lng, geo.address || text);
    } catch {
      toast.error("Indirizzo non trovato.");
    }
  };

  return (
    <div className="sm-search" ref={boxRef}>
      <span className="sm-search-icon">{busy ? <span className="sm-spinner" /> : <Icon name="search" size={15} />}</span>
      <input
        className="sm-input"
        name="address-search"
        value={query}
        disabled={!map}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Indirizzo o coordinate, poi Invio"
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <ul className="sm-suggestions">
          {suggestions.map((s) => (
            <li key={s} onMouseDown={(e) => { e.preventDefault(); pick(s); }}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
